import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_V1, fetchApiWithoutBranch } from '../config/api';
import Header from './Header';
import Footer from './Footer';
import './OrderTracking.css';

const OrderTracking = () => {
  const { trackingToken } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrder = async () => {
    // Prevent requests without a valid tracking token
    if (!trackingToken || typeof trackingToken !== 'string') {
      setError('Invalid tracking token');
      setOrder(null);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      setLoading(true);
      const res = await fetchApiWithoutBranch(`${API_V1}/orders/tracking/${trackingToken}`);
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Orden no encontrada');
        }
        throw new Error('Error al cargar la orden');
      }
      const data = await res.json();
      setOrder(data.data ?? data);
    } catch (err) {
      setError(err.message);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingToken]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'payment_verified':
      case 'completed':
        return '#4CAF50'; // Verde
      case 'pending':
      case 'pending_payment':
        return '#FF9800'; // Naranja
      case 'rejected':
      case 'cancelled':
        return '#F44336'; // Rojo
      default:
        return '#757575'; // Gris
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'Aprobado';
      case 'pending':
      case 'pending_payment':
        return 'Pendiente';
      case 'rejected':
        return 'Rechazado';
      case 'payment_verified':
        return 'Pago Verificado';
      case 'completed':
        return 'Completado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status || 'Desconocido';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <main className="order-tracking-container">
          <div className="order-tracking-loading">
            <p>Cargando información de la orden...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="App">
        <Header />
        <main className="order-tracking-container">
          <div className="order-tracking-error">
            <h2>Error</h2>
            <p>{error || 'Orden no encontrada'}</p>
            <button onClick={fetchOrder} className="refresh-btn">
              Intentar de nuevo
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <main className="order-tracking-container">
        <div className="order-tracking-content">
          <div className="order-tracking-header">
            <h1>Seguimiento de Orden</h1>
            <button onClick={fetchOrder} className="refresh-btn" disabled={loading}>
              {loading ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>

          <div className="order-info-card">
            <h2>Información de la Orden</h2>
            <div className="order-info-grid">
              <div className="info-item">
                <span className="info-label">Número de Orden:</span>
                <span className="info-value">#{order.order_id}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Fecha:</span>
                <span className="info-value">{formatDate(order.order_date)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Cliente:</span>
                <span className="info-value">{order.customer?.name || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Tipo de Entrega:</span>
                <span className="info-value">{order.delivery_type || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Total:</span>
                <span className="info-value">€{parseFloat(order.total_amount || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="order-status-card">
            <h2>Estado del Pago</h2>
            <div className="status-display">
              <div
                className="status-badge"
                style={{ backgroundColor: getStatusColor(order.payment_status || order.order_status) }}
              >
                {getStatusText(order.payment_status || order.order_status)}
              </div>
            </div>
            {order.order_status && (
              <div className="status-display">
                <span className="status-label">Estado de la Orden:</span>
                <div
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(order.order_status) }}
                >
                  {getStatusText(order.order_status)}
                </div>
              </div>
            )}
          </div>

          {order.order_items && order.order_items.length > 0 && (
            <div className="order-items-card">
              <h2>Items de la Orden</h2>
              <div className="order-items-list">
                {order.order_items.map((item) => (
                  <div key={item.order_item_id} className="order-item">
                    <div className="item-info">
                      <span className="item-name">
                        {item.product?.name || item.combo?.name || 'Producto'}
                      </span>
                      <span className="item-quantity">Cantidad: {item.quantity}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {order.payments && order.payments.length > 0 && (
            <div className="payments-card">
              <h2>Pagos</h2>
              <div className="payments-list">
                {order.payments.map((payment) => (
                  <div key={payment.payment_id} className="payment-item">
                    <div className="payment-info">
                      <span className="payment-method">{payment.payment_method}</span>
                      <div
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(payment.payment_status) }}
                      >
                        {getStatusText(payment.payment_status)}
                      </div>
                    </div>
                    {payment.payment_date && (
                      <span className="payment-date">{formatDate(payment.payment_date)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OrderTracking;

